import {Component, OnInit, Input, OnDestroy, Output, EventEmitter, ChangeDetectorRef, NgZone} from '@angular/core';
import L, { latLng, polyline, Map, circleMarker, LatLng, LatLngBounds, latLngBounds,
    Polyline, CircleMarker, Marker, tileLayer, FeatureGroup, LatLngExpression } from 'leaflet';

import 'leaflet.tilelayer.pouchdbcached'
import 'leaflet-gpx'
import PouchDB from 'pouchdb';
import { ColorScheme } from '../../../core/color-scheme';
import { MapImageOverlay, MapPointInterface, MapRouteGPXOverlay, MiscLeafNode, Point, MapCellGridOverlay, MapDevice } from '../../../core/models';
import { AppState } from '../../../core';
import { LookupService, ScoutPointPhotoService } from '../../../core/services';
import { BsModalService } from 'ngx-bootstrap/modal';
import { DeviceDetailModalComponent } from '../../components/device-detail-modal/device-detail-modal.component';
import { BehaviorSubject } from 'rxjs';
import { AppConfig } from '../../../../environments/environment';
import { filter, first, skipWhile, take } from 'rxjs/operators';
import { MapImageOverlayComponent } from '../map-image-overlay/map-image-overlay.component';


// workaround for handling leaflet events emitted outside of angular zone
const CHILD_LEAF_CLICK_PREFIX = 'childLeafNodeClick';


@Component({
    selector: 'app-path-plotter',
    templateUrl: './path-plotter.component.html',
    styleUrls: ['./path-plotter.component.scss']
})
export class PathPlotterComponent<PointType extends MapPointInterface> implements OnInit, OnDestroy {

    static readonly MEDIUM_MARKER_RADIUS = 10
    static readonly SMALL_MARKER_RADIUS = 7
    static readonly MARKER_PANE = 'marker';
    static readonly MARKER_Z_INDEX = 500;
    static readonly SELECTED_MARKER_PANE = 'selectedMarker';
    static readonly DEVICE_PANE = 'deviceMarker';
    static readonly DEVICE_COVERAGE_PANE = 'deviceCoverage';

    @Input() markerRadius : number = 11
    @Input() viaPtMarkerRadius : number = 4
    @Input() changeGuard : (current: PointType, acceptFn: ()=>void) => void = (c,a) => a()
    @Input() showRefDataFn : ( pt : PointType ) => void
    @Input() deletePointFn : ( pt : PointType ) => void
    @Input() miscLeafNodeEventFn : ( evt: {srcEvent: MouseEvent, id: string|number, parentId: string|number}) => void
    @Input() canSelectViaPoints : boolean = true
    @Input() selectedIndex : number = 0
    @Input() allowEditMode : boolean = true
    @Input() showZoomControl : boolean = true
    @Input() boundsPadding : number = .05 // for reports to show surroundings
    @Input() hideLayersControl = false
    @Input() locked : boolean
    @Input() blockEvents = false
    @Input() bInfoMarkersVisible = true;
    @Input() set overlay( overlay : MapImageOverlay ) {
        console.log('overlay(jpg=' + overlay?.jpgName + ', jgw=' + overlay?.jgwName+ ')')
        this._overlay = overlay
        this.mapReady.pipe( filter(bReady=>!!bReady), first() ).subscribe(_=>{
            if ( this._overlay == overlay ) {
                this.initOverlay()
            }
        })
    }
    @Input() set gpx( gpx : MapRouteGPXOverlay ) {
        console.log('gpx(gpx=' + gpx?.gpxName + ')')
        this._gpx = gpx
        this.mapReady.pipe( filter(bReady=>!!bReady), first() ).subscribe(_=>{
            if ( this._gpx == gpx ) {
                this.initGPX()
            }
        })
    }

    _overlay : MapImageOverlay
    _gpx : MapRouteGPXOverlay
    leafletOverlay : L.ImageOverlay
    leafletGPX : L.GPX

    @Output() pointSelected = new EventEmitter<PointType>()
    @Output() overlayIntializing = new BehaviorSubject<boolean>(false)
    @Output() gpxIntializing = new BehaviorSubject<boolean>(false)

    options : any
    layersControlCfg = {
        baseLayers: {},
        overlays: {}
    }

    points : PointType[] = []

    map : Map
    private isFullscreen = false;
    private fullscreenControl?: L.Control;
    private fullscreenBtn?: HTMLAnchorElement;
    pathLines : Polyline[]
    pointMarkers : CircleMarker[]
    infoMarkers : Marker[]

    centerLatLng : LatLng = latLng([ 0, 0 ])
    pathBounds : LatLngBounds = latLngBounds([[0,0],[1,1]])

    gridGroup : FeatureGroup<L.Polyline>
    gridHighlightGroup : FeatureGroup<L.Rectangle>
    gridOutlineRect?: L.Rectangle
    highlightedCellRect?: L.Rectangle
    private gridLabelControl?: L.Control
    private gridLabelDiv?: HTMLDivElement
    private currentGridCell?: { row: number, col: number } | null
    private _cellGridOverlay?: MapCellGridOverlay

    @Input() set cellGridOverlay(overlay: MapCellGridOverlay) {
        this._cellGridOverlay = overlay;
        if (this.map) {
            this.initCellGridOverlay();
        }
    }

    // Devices and coverage
    deviceGroup: FeatureGroup<L.Marker>
    deviceCoverageGroup: FeatureGroup<L.Polygon>
    private _devices: MapDevice[] = []
    @Input() set devices(devices: MapDevice[]) {
        this._devices = devices || [];
        if (this.map) {
            this.renderDevices();
        }
    }

    // Misc Leaf filtering properties
    availableCarriers: Set<string> = new Set()
    availableOSTypes: Set<string> = new Set()
    enabledCarriers: Set<string> = new Set()
    enabledOSTypes: Set<string> = new Set()
    private filterControl?: L.Control
    private filterControlDiv?: HTMLDivElement

    @Output() initialized = new BehaviorSubject<boolean>(false)
    @Output() mapReady = new BehaviorSubject<boolean>(false)

    private static instanceCount = 0;
    private instanceId: number;
    leafNodeEventBinding : (evt: any)=>void;

    constructor(
        protected appState: AppState,
        private lookupService: LookupService,
        private photoService: ScoutPointPhotoService,
        private modalService: BsModalService) {
            this.instanceId = ++PathPlotterComponent.instanceCount;
    }

    handleChildLeafNodeEvent( event : CustomEvent ) {
        const srcEvent = event.detail.event;
        const evt = {srcEvent, id: event.detail.id, parentId: event.detail.parentId};
        this.miscLeafNodeEventFn?.( evt );
    }

    ngOnInit(): void {
        this.leafNodeEventBinding = this.handleChildLeafNodeEvent.bind(this);
        document.addEventListener(CHILD_LEAF_CLICK_PREFIX+this.instanceId, this.leafNodeEventBinding);
        this.appState.doWhenReady( async () => {

            let defaultBaseLayerOptions = {
                maxZoom: 22,
                maxNativeZoom: 18,
                useCache: true,
                //saveToCache: true,
                //useOnlyCache: true,
                crossOrigin: false, // tiles.wmflabs.org does not have cors header in response
                attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            };

            const formatUrl = (url:string) => {
                if ( this.appState.isElectron() && !url.startsWith('http') ) {
                    return 'http:' + url
                }
                return url
            }

            if ( !window.PouchDB ) {
                window.PouchDB = PouchDB
            }

            const scoutConfig = await this.lookupService.rxCollection.findOne('scout').exec()

            let mapConfig = { ...AppConfig.mapConfig }

            let couchMapConfig = scoutConfig?.values?.find(v=>v.id=='map')

            if ( couchMapConfig ) {
                mapConfig = { ...mapConfig, ...couchMapConfig }
            }

            let mapLayers = []
            const layerConfigs = mapConfig.baseLayers;
            layerConfigs?.forEach(layerConfig=>{
                const layerOptions = { ...defaultBaseLayerOptions };
                ['maxZoom','maxNativeZoom','crossOrigin','useCache','useOnlyCache','cacheMaxAge','zIndex'].forEach( configKey => {
                    const value = layerConfig[configKey]
                    if ( value !== undefined ) {
                        if ( value === null ) {
                            delete layerOptions[configKey]
                        } else {
                            layerOptions[configKey] = value
                        }
                    }
                })
                const baseLayer = tileLayer( formatUrl(layerConfig.mapUrl), layerOptions)
                mapLayers.push(baseLayer)
                if ( layerConfig.title ) {
                    this.layersControlCfg.overlays[layerConfig.title] = baseLayer
                }
            })

            this.options = {
                layers: mapLayers,
                zoomControl: this.showZoomControl
            };

            this.initialized.next(true)

        })
    }


    ngOnDestroy() {
        document.removeEventListener(CHILD_LEAF_CLICK_PREFIX+this.instanceId, this.leafNodeEventBinding);
        this.removeMiscLeafFilterControl();
    }

    leafletLayersControl : L.Control
    lastZoom = 0

    async onMapReady(map : Map){
        console.log('onMapReady()')
        try {
            if ( this.blockEvents ) {
                map.touchZoom.disable();
                map.doubleClickZoom.disable();
                map.scrollWheelZoom.disable();
                map.boxZoom.disable();
                map.keyboard.disable();
                map.dragging.disable();
                map.tap?.disable();
            }

            this.map = map
            this.map.attributionControl.remove()
            L.control.scale({imperial: false}).addTo(map)

            //await this.initPathOverlay()

            this.gridGroup = L.featureGroup()
            this.gridGroup.addTo(this.map)

            // Highlight group for active cell and overall outline
            this.gridHighlightGroup = L.featureGroup()
            this.gridHighlightGroup.addTo(this.map)

            // Devices and coverage groups
            this.deviceCoverageGroup = L.featureGroup()
            this.deviceCoverageGroup.addTo(this.map)
            this.deviceGroup = L.featureGroup()
            this.deviceGroup.addTo(this.map)

            if ( !this.hideLayersControl ) {
                try {
                    this.layersControlCfg.overlays['Grid'] = this.gridGroup
                    this.layersControlCfg.overlays['Cell Grid'] = this.gridHighlightGroup
                    this.layersControlCfg.overlays['Device Coverage'] = this.deviceCoverageGroup
                    this.layersControlCfg.overlays['Devices'] = this.deviceGroup
                    this.leafletLayersControl = L.control.layers( this.layersControlCfg.baseLayers, this.layersControlCfg.overlays )
                    this.leafletLayersControl.addTo(this.map)
                } catch ( error ) {
                    console.error('Failed adding grid to leaflet LayersControl (map may have been removed from dom)')
                }
            }

            this.map.on('zoomend', (e)=>{
                const zoom = this.map.getZoom()
                if ( this.lastZoom != zoom ) {
                    this.initGridGroup()
                }
                this.lastZoom = zoom
            })

            map.on('overlayadd', (e)=>{
                if ( (e as any).name == 'Grid') {
                    this.gridEnabled = true
                    this.initGridGroup()
                }
                if ( (e as any).name == 'Cell Grid') {
                    this.initCellGridOverlay();
                }
                if ( (e as any).name == 'Devices') {
                    this.renderDevices();
                }
                if ( (e as any).name == 'Device Coverage') {
                    this.renderDevices();
                }
            });

            map.on('overlayremove', (e)=>{
                if ( (e as any).name == 'Grid') {
                    this.gridEnabled = false
                }
                if ( (e as any).name == 'Cell Grid') {
                    this.gridHighlightGroup?.clearLayers();
                    this.removeGridLabelControl();
                }
                if ( (e as any).name == 'Devices') {
                    this.deviceGroup?.clearLayers();
                }
                if ( (e as any).name == 'Device Coverage') {
                    this.deviceCoverageGroup?.clearLayers();
                }
            })
            if (this._gpx?.gpxDataUrl) {
              new L.GPX(this._gpx.gpxDataUrl, { async: true })
                .on("loaded", function (e) {
                  map.fitBounds(e.target.getBounds());
                })
                .addTo(map);
            }
            this.createPanes(map);
            this.addFullscreenControl(map); // also adds Add-Device button side-by-side
            this.addGridLabelControl();
            this.addMiscLeafFilterControl();
            this.bindGridMouseHandlers();
            this.initCellGridOverlay();
            this.renderDevices();
            this.mapReady.next(true);
        } catch ( error ) {
            console.error('Failed initializing leaflet map for scout path')
            console.error(error)
        }
    }

    private addGridLabelControl() {
        if (this.gridLabelControl) return;
        const self = this;
        this.gridLabelControl = new (L.Control as any)({ position: 'bottomleft' });
        (this.gridLabelControl as any).onAdd = function () {
            self.gridLabelDiv = L.DomUtil.create('div', 'leaflet-control grid-cell-label') as HTMLDivElement;
            self.gridLabelDiv.style.background = 'rgba(255,255,255,0.8)';
            self.gridLabelDiv.style.padding = '2px 6px';
            self.gridLabelDiv.style.borderRadius = '4px';
            self.gridLabelDiv.style.fontSize = '12px';
            self.gridLabelDiv.innerText = '';
            return self.gridLabelDiv;
        } as any;
        (this.gridLabelControl as any).addTo(this.map);
    }

    private removeGridLabelControl() {
        if (this.gridLabelControl) {
            this.gridLabelControl.remove();
            this.gridLabelControl = undefined;
            this.gridLabelDiv = undefined;
        }
    }

    private addMiscLeafFilterControl() {
        if (this.filterControlDiv || !this.map) return;
        
        // Create the control div directly and append to the map container
        this.filterControlDiv = document.createElement('div');
        this.filterControlDiv.className = 'misc-leaf-filter-control';
        this.filterControlDiv.style.background = 'rgba(255,255,255,0.98)';
        this.filterControlDiv.style.padding = '3px';
        this.filterControlDiv.style.borderRadius = '6px';
        this.filterControlDiv.style.fontSize = '9px';
        this.filterControlDiv.style.display = 'none'; // Initially hidden
        this.filterControlDiv.style.position = 'absolute';
        this.filterControlDiv.style.bottom = '3px';
        this.filterControlDiv.style.left = '50%';
        this.filterControlDiv.style.transform = 'translateX(-50%)';
        this.filterControlDiv.style.zIndex = '1000';
        this.filterControlDiv.style.boxShadow = '0 2px 3px rgba(0,0,0,0.2)';
        this.filterControlDiv.style.pointerEvents = 'auto';
        
        // Get the map container and append our control
        const mapContainer = this.map.getContainer();
        mapContainer.style.position = 'relative'; // Ensure container can hold absolutely positioned children
        mapContainer.appendChild(this.filterControlDiv);
        
        // Prevent map events when interacting with the control
        L.DomEvent.disableClickPropagation(this.filterControlDiv);
        L.DomEvent.disableScrollPropagation(this.filterControlDiv);
    }

    private removeMiscLeafFilterControl() {
        if (this.filterControlDiv && this.filterControlDiv.parentNode) {
            this.filterControlDiv.parentNode.removeChild(this.filterControlDiv);
            this.filterControlDiv = undefined;
        }
        if (this.filterControl) {
            this.filterControl.remove();
            this.filterControl = undefined;
        }
    }

    private setGridLabelText(text: string) {
        if (!this.gridLabelDiv) return;
        this.gridLabelDiv.innerText = text || '';
    }

    private buildFilterControlHTML(): string {
        let html = '<div style="display: flex; flex-direction: row; gap: 3px; align-items: center;">';
        
        if (this.availableCarriers.size > 0) {
            html += '<div style="display: flex; flex-direction: row; gap: 6px; padding: 3px 6px; background: rgba(200, 200, 200, 0.2); border-radius: 4px; border: 1px solid rgba(160, 160, 160, 0.4);">';
            for (const carrier of Array.from(this.availableCarriers).sort()) {
                const checked = this.enabledCarriers.has(carrier) ? 'checked' : '';
                html += `<label style="font-weight: normal; margin: 0; display: flex; align-items: center; cursor: pointer; white-space: nowrap; color: black;"><input type="checkbox" data-type="carrier" data-value="${carrier}" ${checked} style="margin-right: 1px;"> ${carrier}</label>`;
            }
            html += '</div>';
        }
        
        if (this.availableOSTypes.size > 0) {
            html += '<div style="display: flex; flex-direction: row; gap: 6px; padding: 3px 6px; background: rgba(120, 120, 120, 0.2); border-radius: 4px; border: 1px solid rgba(100, 100, 100, 0.4);">';
            for (const os of Array.from(this.availableOSTypes).sort()) {
                const checked = this.enabledOSTypes.has(os) ? 'checked' : '';
                html += `<label style="font-weight: normal; margin: 0; display: flex; align-items: center; cursor: pointer; white-space: nowrap; color: black;"><input type="checkbox" data-type="os" data-value="${os}" ${checked} style="margin-right: 1px;"> ${os}</label>`;
            }
            html += '</div>';
        }
        
        html += '</div>';
        return html;
    }

    private updateFilterControlHTML() {
        if (this.filterControlDiv) {
            this.filterControlDiv.innerHTML = this.buildFilterControlHTML();
            this.bindFilterControlEvents();
        }
    }

    private bindFilterControlEvents() {
        if (!this.filterControlDiv) return;
        
        const checkboxes = this.filterControlDiv.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach(checkbox => {
            checkbox.addEventListener('change', (e) => {
                const target = e.target as HTMLInputElement;
                const type = target.dataset['type'];
                const value = target.dataset['value'];
                
                if (type === 'carrier') {
                    if (target.checked) {
                        this.enabledCarriers.add(value!);
                    } else {
                        this.enabledCarriers.delete(value!);
                    }
                } else if (type === 'os') {
                    if (target.checked) {
                        this.enabledOSTypes.add(value!);
                    } else {
                        this.enabledOSTypes.delete(value!);
                    }
                }
                
                this.refreshMiscLeafVisibility();
            });
        });
    }

    private shouldShowMiscLeaf(miscChild: MiscLeafNode): boolean {
        const hasCarrierFilter = this.availableCarriers.size > 0;
        const hasOSFilter = this.availableOSTypes.size > 0;
        
        if (!hasCarrierFilter && !hasOSFilter) {
            return true; // No filters available, show all
        }
        
        // Check carrier filter
        if (hasCarrierFilter) {
            const carrier = miscChild.data?.carrier;
            if (carrier && !this.enabledCarriers.has(carrier)) {
                return false;
            }
        }
        
        // Check OS filter
        if (hasOSFilter) {
            const os = miscChild.data?.os;
            if (os && !this.enabledOSTypes.has(os)) {
                return false;
            }
        }
        
        return true;
    }

    private collectMiscLeafFilterValues(points: PointType[]) {
        // Clear previous values
        this.availableCarriers.clear();
        this.availableOSTypes.clear();
        
        // Collect unique values from miscChildren
        points?.forEach(pt => {
            pt.miscChildren?.forEach(child => {
                if (child.data?.carrier) {
                    this.availableCarriers.add(child.data.carrier);
                }
                if (child.data?.os) {
                    this.availableOSTypes.add(child.data.os);
                }
            });
        });
        
        console.log(`[PathPlotter] Collected filters - Carriers: ${Array.from(this.availableCarriers)}, OS: ${Array.from(this.availableOSTypes)}`);
        
        // Initialize enabled sets if they're empty (first time)
        if (this.enabledCarriers.size === 0) {
            this.availableCarriers.forEach(carrier => this.enabledCarriers.add(carrier));
        }
        if (this.enabledOSTypes.size === 0) {
            this.availableOSTypes.forEach(os => this.enabledOSTypes.add(os));
        }
        
        // Update filter control visibility and content
        this.updateFilterControlVisibility();
    }

    private updateFilterControlVisibility() {
        if (!this.filterControlDiv) {
            console.log('[PathPlotter] Filter control div not found');
            return;
        }
        
        const hasFilters = this.availableCarriers.size > 0 || this.availableOSTypes.size > 0;
        console.log(`[PathPlotter] Has filters: ${hasFilters}, setting display to: ${hasFilters ? 'flex' : 'none'}`);
        this.filterControlDiv.style.display = hasFilters ? 'flex' : 'none';
        
        if (hasFilters) {
            this.filterControlDiv.innerHTML = this.buildFilterControlHTML();
            this.bindFilterControlEvents();
            console.log('[PathPlotter] Filter control content updated');
        }
    }

    private refreshMiscLeafVisibility() {
        // Need to replot to properly handle line filtering
        this.addPathSegments();
    }

    private bindGridMouseHandlers() {
        if (!this.map) return;
        this.map.on('mousemove', (e: L.LeafletMouseEvent) => {
            if (!this._cellGridOverlay) return;
            const cell = this.getCellAtLatLng(e.latlng);
            if (cell) {
                this.currentGridCell = cell;
                this.setGridLabelText(`Cell r:${cell.row} c:${cell.col}`);
                this.updateHighlightedCell(cell);
            } else {
                this.currentGridCell = null;
                this.setGridLabelText('');
                this.clearHighlightedCell();
            }
        });
        this.map.on('mouseout', () => {
            this.currentGridCell = null;
            this.setGridLabelText('');
            this.clearHighlightedCell();
        });
    }

    private initCellGridOverlay() {
        try {
            this.gridHighlightGroup?.clearLayers();
            this.clearHighlightedCell(false);
            if (!this._cellGridOverlay || !this.map) return;
            // Draw overall grid outline for context
            const { originLat, originLng, cellWidthMeters, cellHeightMeters, rows, cols } = this._cellGridOverlay;
            const origin = L.latLng(originLat, originLng);
            const topLeft = origin;
            const bottomRight = this.offsetMetersToLatLng(origin, cols * cellWidthMeters, rows * cellHeightMeters);
            const outlineBounds = L.latLngBounds(topLeft, bottomRight);
            this.gridOutlineRect = L.rectangle(outlineBounds as L.LatLngBoundsExpression, { color: '#2b8a3e', weight: 1, dashArray: '4,4', fill: false });
            this.gridHighlightGroup.addLayer(this.gridOutlineRect);
            this.gridHighlightGroup.bringToFront();
        } catch (err) {
            console.error('Failed initializing cell grid overlay', err);
        }
    }

    private clearHighlightedCell(removeLayer: boolean = true) {
        if (this.highlightedCellRect && removeLayer) {
            this.gridHighlightGroup.removeLayer(this.highlightedCellRect);
        }
        this.highlightedCellRect = undefined;
    }

    private updateHighlightedCell(cell: { row: number; col: number }) {
        if (!this._cellGridOverlay) return;
        const { originLat, originLng, cellWidthMeters, cellHeightMeters } = this._cellGridOverlay;
        const origin = L.latLng(originLat, originLng);
        const cellTopLeft = this.offsetMetersToLatLng(origin, cell.col * cellWidthMeters, cell.row * cellHeightMeters);
        const cellBottomRight = this.offsetMetersToLatLng(origin, (cell.col + 1) * cellWidthMeters, (cell.row + 1) * cellHeightMeters);
        const rectBounds = L.latLngBounds(cellTopLeft, cellBottomRight);
        if (!this.highlightedCellRect) {
            this.highlightedCellRect = L.rectangle(rectBounds as L.LatLngBoundsExpression, { color: '#1c7ed6', weight: 2, fill: true, fillOpacity: 0.08 });
            this.gridHighlightGroup.addLayer(this.highlightedCellRect);
        } else {
            this.highlightedCellRect.setBounds(rectBounds as any);
        }
        this.gridHighlightGroup.bringToFront();
    }

    private getCellAtLatLng(latlng: L.LatLng): { row: number; col: number } | null {
        if (!this._cellGridOverlay) return null;
        const { originLat, originLng, cellWidthMeters, cellHeightMeters, rows, cols } = this._cellGridOverlay;
        const origin = L.latLng(originLat, originLng);

        // Compute signed distances (meters) east and south from origin
        const dx = this.eastMetersBetween(origin, latlng);
        const dy = this.southMetersBetween(origin, latlng);
        if (dx < 0 || dy < 0) return null;
        const col = Math.floor(dx / cellWidthMeters);
        const row = Math.floor(dy / cellHeightMeters);
        if (col < 0 || row < 0 || col >= cols || row >= rows) return null;
        return { row, col };
    }

    private eastMetersBetween(a: L.LatLng, b: L.LatLng): number {
        // Distance along latitude line; sign by longitude comparison
        const sameLatA = L.latLng(a.lat, a.lng);
        const sameLatB = L.latLng(a.lat, b.lng);
        const d = sameLatA.distanceTo(sameLatB);
        return b.lng >= a.lng ? d : -d;
    }

    private southMetersBetween(a: L.LatLng, b: L.LatLng): number {
        // Distance along longitude line; sign by latitude comparison
        const sameLngA = L.latLng(a.lat, a.lng);
        const sameLngB = L.latLng(b.lat, a.lng);
        const d = sameLngA.distanceTo(sameLngB);
        return b.lat <= a.lat ? d : -d;
    }

    private offsetMetersToLatLng(origin: L.LatLng, eastMeters: number, southMeters: number): L.LatLng {
        const proj = L.CRS.EPSG3857;
        const p = proj.project(origin);
        const cosLat = Math.cos(origin.lat * Math.PI / 180);
        const x2 = p.x + (eastMeters / (cosLat || 1e-6));
        const y2 = p.y - southMeters;
        return proj.unproject(L.point(x2, y2));
    }

    // ============== Device coverage ==============
    private renderDevices() {
        try {
            this.deviceGroup?.clearLayers();
            this.deviceCoverageGroup?.clearLayers();
            if (!this._devices || !this.map) return;
            for (const d of this._devices) {
                if (d.latitude == null || d.longitude == null) continue;
                // Icon marker with rotation and positioning based on device configuration
                const iconClass = d.iconClass || this.getIconForCategory(d.category as any);
                const color = d.color || '#e03131';
                // Will hold the coverage group for this device (if any)
                let coverageGroupForThisDevice: L.FeatureGroup | undefined;
                
                // Build style string with color and font size
                let styleString = `color:${color}; font-size: 24px;`;
                
                // Handle rotation and anchor positioning
                // Apply rotation whenever a rotation is defined unless explicitly disabled
                if (d.iconRotationDegrees !== undefined && d.shouldRotateIcon !== false) {
                    const anchor = d.iconAnchor || 'bottom-center';
                    const transformOrigin = anchor === 'center' ? '50% 50%' : '50% 100%';
                    styleString += ` transform-origin: ${transformOrigin}; transform: rotate(${d.iconRotationDegrees}deg);`;
                } else if (d.iconAnchor) {
                    // Set transform origin for positioning even without rotation
                    const transformOrigin = d.iconAnchor === 'center' ? '50% 50%' : '50% 100%';
                    styleString += ` transform-origin: ${transformOrigin};`;
                } else {
                    // Legacy fallback: use bottom-center for vertex positioning
                    styleString += ` transform-origin: 50% 100%;`;
                }
                
                const html = `<i class="${iconClass}" style="${styleString}"></i>`;
                
                // Calculate icon anchor based on device configuration
                let iconAnchor: [number, number];
                const baseIconSize = 24;
                const offsetX = d.iconOffsetX || 0;
                const offsetY = d.iconOffsetY || 0;
                
                if (d.iconAnchor === 'center') {
                    // Center anchor: middle of icon
                    iconAnchor = [baseIconSize / 2 + offsetX, baseIconSize / 2 + offsetY];
                } else {
                    // Bottom-center anchor (default): bottom-center of icon
                    iconAnchor = [baseIconSize / 2 + offsetX, baseIconSize - 2 + offsetY];
                }
                
                const icon = L.divIcon({ 
                    html, 
                    iconSize: [baseIconSize, baseIconSize], 
                    iconAnchor,
                    className: 'device-icon' 
                });
                const title = this.buildDeviceTitle(d);
                const marker = L.marker([d.latitude, d.longitude], { 
                    icon, 
                    pane: PathPlotterComponent.DEVICE_PANE
                });
                marker.bindTooltip(title, { sticky: true });
                
                // Add click and hover highlight; also reflect on coverage (direct style changes)
                marker.on('click', () => this.showDeviceDetails(d));
                marker.on('mouseover', () => {
                    const el = marker.getElement();
                    if (el) el.classList.add('hovered');
                    // Highlight matching coverage (slightly yellow)
                    if (coverageGroupForThisDevice) {
                        coverageGroupForThisDevice.eachLayer((layer: any) => {
                            if (layer instanceof L.Polygon) {
                                layer.setStyle({ fillOpacity: 0.22, fillColor: 'rgba(255, 255, 0, 0.35)' });
                            } else if (layer instanceof L.Polyline) {
                                layer.setStyle({ weight: 3 });
                            }
                        });
                    }
                });
                marker.on('mouseout', () => {
                    const el = marker.getElement();
                    if (el) el.classList.remove('hovered');
                    // Revert coverage style
                    if (coverageGroupForThisDevice) {
                        coverageGroupForThisDevice.eachLayer((layer: any) => {
                            if (layer instanceof L.Polygon) {
                                layer.setStyle({ fillOpacity: 0.12, fillColor: color });
                            } else if (layer instanceof L.Polyline) {
                                layer.setStyle({ weight: 2 });
                            }
                        });
                    }
                });
                
                marker.addTo(this.deviceGroup);

                // Coverage sector - only plot if range is explicitly specified
                const hasRange = d.range_meters && d.range_meters > 0;
                const hasBeamWidth = (d.azimuth_beam_width || 0) > 0;
                const hasAzimuth = (d.azimuthDeg !== undefined) || (d.azimuth !== undefined);
                
                console.log(`[renderDevices] Device ${d.id} coverage check:`, {
                    range_meters: d.range_meters,
                    azimuth_beam_width: d.azimuth_beam_width,
                    azimuth: d.azimuth,
                    azimuthDeg: d.azimuthDeg,
                    hasRange,
                    hasBeamWidth,
                    hasAzimuth,
                    willRenderCoverage: hasRange && hasBeamWidth && hasAzimuth
                });
                
                if (hasRange && hasBeamWidth && hasAzimuth) {
                    const azimuth = d.azimuthDeg ?? d.azimuth ?? 0;
                    const sectorGroup = this.createCoverageSector(L.latLng(d.latitude, d.longitude), d.range_meters!, azimuth, d.azimuth_beam_width!);
                    coverageGroupForThisDevice = sectorGroup;

                    // Style, tooltip, click, and hover behavior for coverage sector
                    const title = this.buildDeviceTitle(d);
                    sectorGroup.eachLayer((layer: any) => {
                        if (layer instanceof L.Polygon) {
                            layer.setStyle({ 
                                fillColor: color, 
                                fillOpacity: 0.08 
                            });
                        }
                        // Bind hover tooltip and click handler on all child layers
                        if (typeof layer.bindTooltip === 'function') {
                            layer.bindTooltip(title, {sticky: true});
                        }
                        layer.on('click', () => this.showDeviceDetails(d));
                        layer.on('mouseover', () => {
                            if (layer instanceof L.Polygon) {
                                layer.setStyle({ fillOpacity: 0.28, fillColor: 'rgba(255, 255, 0, 0.6)' });
                            } else if (layer instanceof L.Polyline) {
                                layer.setStyle({ weight: 3, color: 'rgba(255, 255, 0, 0.95)' });
                            }
                        });
                        layer.on('mouseout', () => {
                            if (layer instanceof L.Polygon) {
                                layer.setStyle({ fillOpacity: 0.08, fillColor: color });
                            } else if (layer instanceof L.Polyline) {
                                layer.setStyle({ weight: 2, color: 'rgba(255, 255, 0, 0.6)' });
                            }
                        });
                    });

                    sectorGroup.addTo(this.deviceCoverageGroup);
                }
            }
        } catch (err) {
            console.error('Failed rendering devices', err);
        }
    }

    private showDeviceDetails(device: MapDevice) {
        DeviceDetailModalComponent.show(this.modalService, {
            device,
            title: 'Device Details'
        });
    }

    private buildDeviceTitle(d: MapDevice): string {
        const parts: string[] = [];
        if (d.id) {
            const mountType = d.data?.mount_type;
            const idText = mountType ? `ID: ${d.id} (${mountType})` : `ID: ${d.id}`;
            parts.push(idText);
        }
        const az = (d.azimuthDeg ?? d.azimuth);
        if (az !== undefined) parts.push(`Azimuth: ${Math.round(az)}°`);
        if (d.tilt !== undefined) {
            const dir = d.tilt > 0 ? 'Upward' : d.tilt < 0 ? 'Downward' : undefined;
            const tiltTxt = `${Math.abs(Math.round(d.tilt))}°${dir ? ' ' + dir : ''}`;
            parts.push(`Tilt: ${tiltTxt}`);
        }
        const h = (d.altitudeMeters ?? d.height);
        if (h !== undefined) parts.push(`Height: ${h}m`);
        return parts.join(' | ') || 'Device';
    }

    private createCoverageSector(center: L.LatLng, radiusMeters: number, azimuthDeg: number, beamwidthDeg: number, steps: number = 64): L.FeatureGroup {
        console.log(`[createCoverageSector] Input:`, { radiusMeters, azimuthDeg, beamwidthDeg });
        
        const start = azimuthDeg - beamwidthDeg / 2;
        const end = azimuthDeg + beamwidthDeg / 2;
        const clampAngle = (a: number) => ((a % 360) + 360) % 360;
        const a0 = clampAngle(start);
        let a1 = clampAngle(end);
        
        // Handle wrap-around across 360 and fix 360° case
        let totalSpan;
        if (beamwidthDeg >= 360) {
            totalSpan = 360; // Full circle for 360° or greater
        } else {
            totalSpan = (a1 >= a0) ? (a1 - a0) : (360 - (a0 - a1));
            totalSpan = Math.max(totalSpan, 0.1);
        }
        console.log(`[createCoverageSector] Calculated totalSpan: ${totalSpan} for beamwidthDeg: ${beamwidthDeg}`);
        const n = Math.max(8, Math.round((steps * totalSpan) / 360));
        
        // Create filled sector (no border)
        const sectorPoints: L.LatLng[] = [];
        sectorPoints.push(center); // wedge from center
        for (let i = 0; i <= n; i++) {
            const frac = i / n;
            const angle = (a0 + frac * totalSpan);
            const rad = angle * Math.PI / 180;
            // Convert to East=0° coordinate system (instead of North=0°)
            // 0° is east (positive X), 90° is north (negative Y)
            const dx = radiusMeters * Math.cos(rad);
            const dy = -radiusMeters * Math.sin(rad);
            sectorPoints.push(this.offsetMetersToLatLng(center, dx, dy));
        }
        
        const featureGroup = L.featureGroup();
        
        // Add filled sector with no border
        const filledSector = L.polygon(sectorPoints, { 
            color: 'transparent', 
            weight: 0, 
            fillColor: '', 
            fillOpacity: 0,
            pane: PathPlotterComponent.DEVICE_COVERAGE_PANE
        });
        featureGroup.addLayer(filledSector);
        
        // Add side lines with yellow color (skip for omnidirectional 360° coverage)
        if (beamwidthDeg < 360) {
            const startAngleRad = (a0 * Math.PI) / 180;
            const endAngleRad = (a1 * Math.PI) / 180;
            
            const startDx = radiusMeters * Math.cos(startAngleRad);
            const startDy = -radiusMeters * Math.sin(startAngleRad);
            const startPoint = this.offsetMetersToLatLng(center, startDx, startDy);
            
            const endDx = radiusMeters * Math.cos(endAngleRad);
            const endDy = -radiusMeters * Math.sin(endAngleRad);
            const endPoint = this.offsetMetersToLatLng(center, endDx, endDy);
            
            // Left side line
            const leftLine = L.polyline([center, startPoint], { 
                color: 'rgba(255, 255, 0, 0.6)', 
                weight: 2,
                pane: PathPlotterComponent.DEVICE_COVERAGE_PANE
            });
            featureGroup.addLayer(leftLine);
            
            // Right side line  
            const rightLine = L.polyline([center, endPoint], { 
                color: 'rgba(255, 255, 0, 0.6)', 
                weight: 2,
                pane: PathPlotterComponent.DEVICE_COVERAGE_PANE
            });
            featureGroup.addLayer(rightLine);
        }
        
        return featureGroup;
    }



    private getIconForCategory(category?: string): string {
        switch ((category || '').toLowerCase()) {
            case 'satellite-dish':
            case 'satellite':
                return 'fa fa-globe';
            case 'radio-antenna':
            case 'antenna':
            default:
                return 'fa fa-wifi';
        }
    }

    private addFullscreenControl(map: L.Map) {
        const self = this;
        const Controls = (L.Control as any).extend({
            options: { position: 'topright' },
            onAdd: function () {
                const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-maximize');
                // Fullscreen button
                const fsBtn = L.DomUtil.create('a', 'maximize-btn', container) as HTMLAnchorElement;
                fsBtn.href = '#';
                fsBtn.setAttribute('role', 'button');
                fsBtn.title = self.isFullscreen ? 'Exit full screen' : 'Full screen';
                fsBtn.innerHTML = self.isFullscreen ? '<i class="fa fa-compress"></i>' : '<i class="fa fa-expand"></i>';
                L.DomEvent.on(fsBtn, 'click', L.DomEvent.stop)
                    .on(fsBtn, 'click', () => { self.toggleFullscreen(); });
                self.fullscreenBtn = fsBtn;

                return container;
            }
        });
        this.fullscreenControl = new Controls();
        this.fullscreenControl.addTo(map);
    }

    private updateFullscreenBtn() {
        if (!this.fullscreenBtn) return;
        this.fullscreenBtn.title = this.isFullscreen ? 'Exit full screen' : 'Full screen';
        this.fullscreenBtn.innerHTML = this.isFullscreen ? '<i class="fa fa-compress"></i>' : '<i class="fa fa-expand"></i>';
    }

    private toggleFullscreen() {
        const container = this.map?.getContainer();
        if (!container) return;
        this.isFullscreen = !this.isFullscreen;
        if (this.isFullscreen) {
            L.DomUtil.addClass(container, 'fullscreen-map');
        } else {
            L.DomUtil.removeClass(container, 'fullscreen-map');
        }
        this.updateFullscreenBtn();
        // Give the browser a tick to apply layout before invalidating size
        setTimeout(() => this.map?.invalidateSize(true), 0);
    }

    createPanes(map: L.Map){
        map.createPane(PathPlotterComponent.MARKER_PANE).style.zIndex =
        `${PathPlotterComponent.MARKER_Z_INDEX}`;
        map.createPane(PathPlotterComponent.SELECTED_MARKER_PANE).style.zIndex =
        `${PathPlotterComponent.MARKER_Z_INDEX+10}`;
        map.createPane(PathPlotterComponent.DEVICE_COVERAGE_PANE);
        map.createPane(PathPlotterComponent.DEVICE_PANE);
    }

    gridEnabled = true

    async initOverlay() {
        const pathOverlay = this._overlay

        // wait if another overlay load is in progress...
        await this.overlayIntializing.pipe(filter(bInitializing=>!bInitializing),first()).toPromise()
        this.overlayIntializing.next(true)

        if ( this.leafletOverlay ) {
            this.leafletOverlay.removeFrom(this.map)
            this.leafletOverlay = null
            delete this.layersControlCfg.overlays['Overlay']
        }

        try {
            if ( !pathOverlay ) {
                return
            }

            let { imageDataUrl, topLeft: worldTopLeft, pixelSize: worldPixelSize, imageSize } = pathOverlay

            if ( !worldTopLeft || !worldPixelSize ) {
                return
            }

            if ( !imageDataUrl ) {
                const imageDoc = await this.photoService.rxCollection.findOne(pathOverlay.imageId).exec()
                if ( imageDoc ) {
                    imageDataUrl = await this.photoService.loadLocalPhotoFile(imageDoc._id,imageDoc.fileChecksum)
                    if ( !imageDataUrl ) {
                        imageDataUrl = await this.photoService.downloadScoutRefPhotoFile(imageDoc._id,imageDoc.mimeType)
                    }
                }
            }

            if ( !imageDataUrl ) {
                console.warn('no image overlay image found.  imageId=' + pathOverlay?.imageId)
                return
            } else if ( this._overlay != pathOverlay ) {
                console.warn('image overlay loaded but a new overlay has already been assigned')
                return
            }

            //TBD - use pre-computed size or always compute
            imageSize = await MapImageOverlayComponent.getImageSize( imageDataUrl )
            const overlayBounds = MapImageOverlayComponent.computeBounds(worldTopLeft,worldPixelSize,imageSize)
            const bounds = [
                [ overlayBounds.topLeft.x, overlayBounds.topLeft.y ],
                [ overlayBounds.bottomRight.x, overlayBounds.bottomRight.y]
            ]
            this.leafletOverlay = L.imageOverlay(imageDataUrl, bounds as any)
            this.leafletOverlay.addTo(this.map)
            this.layersControlCfg.overlays['Overlay'] = this.leafletOverlay            
        } catch ( error ) {
            if ( /^Cannot read propert(y|ies) of undefined.*/.test(error?.message) ) {
                console.warn('Leaflet map overlay init failed (map may have been removed from page before image loaded)')
                console.warn(error?.message)
            } else if ( error?.message == 'Image load failed: Not Found' ) {
                this.photoService.publishError('Image Overlay not found: ' + pathOverlay?.jpgName, this)
            } else {
                console.error(error?.message)
                this.photoService.publishError(error,this)
            }
        } finally {
            if ( this.leafletLayersControl ) {
                this.leafletLayersControl.remove()
                this.leafletLayersControl = L.control.layers( this.layersControlCfg.baseLayers, this.layersControlCfg.overlays )
                try {
                    this.leafletLayersControl.addTo(this.map)
                } catch ( error: any ) {
                    if ( !/^Cannot read propert(y|ies) of undefined.*/.test(error?.message) ) {
                        console.error('Leaflet map overlay load/render failed')
                        throw error
                    }
                }
            }
            this.overlayIntializing.next(false)
        }
    }

    async initGPX() {
        console.log("initGPX()")
        const gpxRoute = this._gpx
        // wait if another overlay load is in progress...
        await this.gpxIntializing.pipe(filter(bInitializing=>!bInitializing),first()).toPromise()
        this.gpxIntializing.next(true)

        if ( this.leafletGPX ) {
            this.leafletGPX.removeFrom(this.map)
            this.leafletGPX = null
            delete this.layersControlCfg.overlays['Gpx']
        }

        try {
            if ( !gpxRoute ) {
                return
            }

            let { gpxDataUrl } = gpxRoute

            this.leafletGPX = new L.GPX(gpxDataUrl)
            this.leafletGPX.addTo(this.map)
            this.layersControlCfg.overlays['Gpx'] = this.leafletGPX
        } catch ( error ) {
            if ( /^Cannot read propert(y|ies) of undefined.*/.test(error?.message) ) {
                console.warn('Leaflet map gpx init failed (map may have been removed from page before image loaded)')
                console.warn(error?.message)
            }
        } finally {
            if ( this.leafletLayersControl ) {
                this.leafletLayersControl.remove()
                this.leafletLayersControl = L.control.layers( this.layersControlCfg.baseLayers, this.layersControlCfg.overlays )
                try {
                    this.leafletLayersControl.addTo(this.map)
                } catch ( error: any ) {
                    if ( !/^Cannot read propert(y|ies) of undefined.*/.test(error?.message) ) {
                        console.error('Leaflet map overlay load/render failed')
                        throw error
                    }
                }
            }
            this.gpxIntializing.next(false)
        }
    }


    plot( points : PointType[] ) {
        this.points = points
        try {
            if( points ) {
                // Collect unique carriers and OS types from miscChildren
                this.collectMiscLeafFilterValues(points);
                
                const oldBounds = this.pathBounds
                this.pathBounds = this.getPathBounds()
                const boundsChanged = this.pathBounds && !this.pathBounds.equals(oldBounds)
                if ( boundsChanged ) {
                    if ( this.map ) {
                        this.initGridGroup()
                    } else {
                        this.mapReady.pipe(filter(bReady=>!!bReady), take(1)).subscribe(_=>this.initGridGroup())
                    }
                }
                this.centerLatLng = this.getCenterPoint(this.pathBounds)
            }
            this.addPathSegments()
        } catch ( error ) {
            console.error(error)
            this.appState.publishError('Failed plotting path in map. ' + error?.message)
        }
    }

    refresh() {
        this.plotWhenReady(this.points,{invalidateSize:true});
    }

    plotWhenReady( points: PointType[] = null, opts: { invalidateSize?: boolean } = null ) {    
        if ( points ) {
            this.points = points
        }
        this.mapReady.pipe( skipWhile(bReady=>!bReady), take(1) ).subscribe({
            next: (bReady:boolean) => {
                if ( opts?.invalidateSize ) {
                    this.map.invalidateSize()
                }
                if ( this.points == points ) {
                    this.plot(points)
                }
            },
            error: (err:any) => {
                console.error(err)
                this.appState.publishError(err,this)
            }
        })
    }


    getCenterPoint( bounds : LatLngBounds) {
        let center : LatLng = null
        if ( !bounds ) {
            center = latLng(0,0)
        } else {
            center = bounds.getCenter()
        }
        return center
    }


    getPathBounds() {
        let bounds : LatLngBounds = null
        try {
            if ( this.points?.length ) {
                const validPoints = this.points.filter(pt=>!!pt.latitude&&!!pt.longitude)
                const latLngs = validPoints.map(pt=>latLng(Number(pt.latitude),Number(pt.longitude)))
                validPoints.forEach(pt=>{
                    pt.miscChildren?.forEach(child=>{
                        if ( child.latitude && child.longitude ) {
                            latLngs.push(latLng(Number(child.latitude),Number(child.longitude)))
                        }
                    });
                });
                bounds = latLngBounds(latLngs).pad(this.boundsPadding)

                // latLngBounds does not work if single point or all points same
                if ( bounds.getNorth()==bounds.getSouth() && bounds.getEast()==bounds.getWest()) {
                    bounds = bounds.getNorthEast().toBounds(10).pad(this.boundsPadding)
                }
            }
        } catch ( error ) {
            console.warn('Could not compute bounds for path. ' + error.message)
        }
        //console.log('getPathBounds()=' + bounds?.toBBoxString())
        if ( !bounds ) {
            bounds = new L.LatLngBounds(latLng(0,0),latLng(0,0))
        }
        return bounds
    }


    markerPopupInitFn = (startPoint:PointType) => (fl:any) => Object;


    getColorFn = (point:PointType,index:number) => ColorScheme.pointColorForIndex(index)

    addPathSegments(startPt : PointType = null, reachedPoints: any = {}) {
        const points = this.points
        const pathStartPoints = points?.length == 1 ? points : points?.filter(pt=>!pt.parentId && (pt.latitude !== undefined && pt.longitude !== undefined));
        const pathStartPoint = pathStartPoints && pathStartPoints[0];
        if ( startPt == null ) {
            this.pathLines = []
            this.pointMarkers = []
            this.infoMarkers = []
            if ( pathStartPoint ) {
                // start from each pt without a parent (should be just one but handle disconnected sections too)
                pathStartPoints.forEach( pt => this.addPathSegments(pt) )
            }
            return
        }

        if ( reachedPoints[startPt.id] ) {
            console.warn('Cyclical point reference in path: ' + startPt.id)
            return
        } else {
            reachedPoints[startPt.id] = startPt
        }

        const childPoints = points.filter( pt=>pt.parentId == startPt.id )
        const pathStartOrderIndex = points.findIndex(pt=>pt.id==pathStartPoint?.id)
        const index = points.findIndex(pt=>pt.id==startPt.id)
        const color = this.getColorFn(startPt,index-pathStartOrderIndex)

        const isViaPt = startPt.type=='VIA'
        const isHaOrigin = startPt.id == pathStartPoint?.id

        this.generateMapPoint(startPt,index,color,isViaPt,isHaOrigin)

        //console.log('generateLayers buiding ' + destPoints.length + ' segments from lat=' + startPt.latitude + ', lng=' + startPt.longitude)
        for ( let childPoint of childPoints ) {
            const childIndex = points.findIndex(pt=>pt.id==childPoint.id)
            if ( startPt.latitude !== undefined && startPt.longitude !== undefined
                && childPoint.latitude !== undefined && childPoint.longitude !== undefined ) {
                    this.addPathLine(startPt,childPoint,childIndex-pathStartOrderIndex);
            }
            this.addPathSegments(childPoint,reachedPoints)
        }
        for ( let child of startPt.miscChildren || [] ) {
            const bCanPlot = child.latitude != null && child.longitude != null
                && !(isNaN(child.latitude) || isNaN(child.longitude));
            if ( bCanPlot ) {
                this.plotMiscLeafChild(startPt,index,child,color);
            }
        }
    }

    highlightLeafNode(leafNodeId:number|string,bHighlight:boolean) {
        const id = `marker-div-${this.instanceId}-${leafNodeId}`;
        const pane = this.map?.getPane('mapPane');
        const div = pane?.querySelector(`#${id}`)?.parentElement;
        if ( div ) {
            if ( bHighlight) {
                div.classList.add('highlight-leaf-node');
                div.style.zIndex = `${Number(div.style.zIndex) + 1000}`;
            } else {
                div.classList.remove('highlight-leaf-node');
                div.style.zIndex = `${Number(div.style.zIndex) - 1000}`;
            }
        }

    }

    plotMiscLeafChild( pt: PointType, ptIndex: number, miscChild: MiscLeafNode, color: string){
        // Check if this miscLeaf child should be visible based on filters
        const shouldShow = this.shouldShowMiscLeaf(miscChild);
        
        // Only create line and marker if the miscLeaf child should be shown
        if (shouldShow) {
            this.pathLines.push( polyline(
                [[pt.latitude, pt.longitude],[miscChild.latitude,miscChild.longitude]],
                {color, weight:3, opacity: 0.2, dashArray: [1, 10],}
            ))
            
            const isSelected = ptIndex == this.selectedIndex;
            let iconClass = miscChild.iconClass || 'fa fa-star phone-icon';
            const evtName = CHILD_LEAF_CLICK_PREFIX+this.instanceId;

            //TODO - Building the below js is a hack for click, mouseOver, and mouseOut
            //       Make one global function (or angular entryComponent)
            const evtOptions = `{detail:{event,parentId:${pt.id==null?'null':pt.id},id:${miscChild.id==null?'null':miscChild.id},type:'${miscChild.type}'}}`;
            const clickAttr = `onclick="document.dispatchEvent(new CustomEvent('${evtName}',${evtOptions}))"`;
            const mouseOverAttr = `onmouseover="document.dispatchEvent(new CustomEvent('${evtName}',${evtOptions}))"`;
            const mouseOutAttr = `onmouseout="document.dispatchEvent(new CustomEvent('${evtName}',${evtOptions}))"`;
            const styleAttr = `style="border:solid 2px ${color};"`;
            const iconElement = `<i class="${iconClass}"></i>`;
            const divId = `marker-div-${this.instanceId}-${miscChild.id}`;
            const html = `<div id='${divId}' role="button" ${clickAttr} ${mouseOverAttr} ${mouseOutAttr} ${styleAttr}>${iconElement}<div>`;
            let markerIcon = L.divIcon({ html,
                iconSize: [20, 20],
                className: 'misc-marker-leaf-outer-div' + (isSelected ? ' selected-misc-marker-leaf-outer-div' : ''),
            })
            const extraMarkerOpts : Partial<L.MarkerOptions> = { pane: isSelected ?
                PathPlotterComponent.SELECTED_MARKER_PANE : PathPlotterComponent.MARKER_PANE,
                zIndexOffset: 1000,
                riseOnHover: true,
            };
            const marker = L.marker([miscChild.latitude, miscChild.longitude],{
                icon: markerIcon, ...extraMarkerOpts
            })
            if (miscChild.title) {
                (marker as any).bindTooltip(miscChild.title, { sticky: true });
            }
            this.pointMarkers.push(marker as any);
        }
    }

    addPathLine( startPt: PointType, childPt: PointType, indexFromPathStart: number ){
        this.pathLines.push( polyline(
            [[startPt.latitude, startPt.longitude],[childPt.latitude,childPt.longitude]],
            {color: this.getColorFn(childPt,indexFromPathStart), weight:3}
        ))
    }

    getMarkerFillColor( pt: PointType ){
        return "white"
    };
    getSmallMarkerFillColor( pt: PointType, lineColor: string ){
        return lineColor
    };

    generateMapPoint(startPt:PointType,index:number,color:string,isViaPt:boolean,isHaOrigin:boolean){
        let radius = isViaPt ? this.viaPtMarkerRadius : this.markerRadius
        let weight = 2
        let fillColor;
        const isSelected = index == this.selectedIndex

        if ( isSelected ) {
            fillColor = "yellow"
        } else if ( radius < PathPlotterComponent.SMALL_MARKER_RADIUS ) {
            fillColor = this.getSmallMarkerFillColor(startPt,color);
        } else {
            fillColor = this.getMarkerFillColor(startPt)
        }

        if ( isHaOrigin ) {
            weight = 3
        }

        if ( radius > 0 ) {
            if ( isSelected ) {
                radius += 1;
            }
        }

        let marker;
        const extraMarkerOpts : Partial<L.MarkerOptions> = { pane: isSelected ?
            PathPlotterComponent.SELECTED_MARKER_PANE : PathPlotterComponent.MARKER_PANE
        };
        if ( radius > 0 ) {
            // add shadow for main circle marker
            if ( startPt.latitude !== undefined && startPt.longitude !== undefined ) {
                marker = this.buildCircleMarker(startPt,
                    [startPt.latitude, startPt.longitude],
                    {radius,color,weight,fillColor,fillOpacity:1,...extraMarkerOpts},
                    isSelected);
            }
        } else {
            // use icon if not a circle marker
            let iconClass = "fa fa-bullseye fa-2x";
            if ( isViaPt ) {
                iconClass = "fa fa-paw fa-1x";
            }
            let markerIcon = L.divIcon({ html: `<i class="${iconClass}" style="color:${color};"></i>`,
                iconSize: [20, 20],
                className: 'marker-icon'
            })
            marker = L.marker([startPt.latitude, startPt.longitude],{ icon: markerIcon, ...extraMarkerOpts})
        }

        const isHaPt = startPt.type=='HA'

        if ( marker) {
            let tooltipClasses = []
            let tooltipDirection: L.Direction = "center"

            if ( isHaPt && !isHaOrigin ) {
                tooltipClasses.push('underlined-tooltip')
            }

            if ( isSelected ) {
                tooltipClasses.push('selected-point-tooltip')
            }
            if ( radius >= PathPlotterComponent.MEDIUM_MARKER_RADIUS ) {
                tooltipClasses.push("point-tooltip")
            } else if ( radius >= PathPlotterComponent.SMALL_MARKER_RADIUS ) {
                tooltipClasses.push("point-sm-tooltip")
            } else {
                // put label above point because not circle marker or too small for internal text
                tooltipDirection = "top"
                tooltipClasses.push( isSelected  ? "selected-point-xs-tooltip" : "point-xs-tooltip")
            }
            this.bindMarkerTooltip(marker,startPt,{
                ...extraMarkerOpts,
                permanent: true,
                direction: tooltipDirection,
                className: tooltipClasses.join(' ')
            },isSelected);
        }

        this.addInfoMarkerLabel(startPt,{isViaPt,isHaPt,isSelected},extraMarkerOpts);

        if ( marker && this.markerPopupInitFn ) {
            if ( !this.blockEvents ) {
                marker.bindPopup( this.markerPopupInitFn(startPt) )
            }
            this.pointMarkers.push(marker)
        }
    }

    // volunteer and path test points override this to check for status "not using"
    isMuted(pt:PointType){
        return pt && pt.disabled;
    }

    buildCircleMarker(pt:PointType, latLng : LatLngExpression, markerOpts: L.CircleMarkerOptions, isSelected:boolean){

        //TODO - can outer marker be replaced by CSS style?
        const outerMarkerOpts = {...markerOpts, color: 'gray', weight: 2, opacity:0.25, radius: markerOpts.radius+2,fillOpacity:0}
        this.pointMarkers.push(circleMarker(latLng,outerMarkerOpts));

        if ( this.isMuted(pt) ) {
            markerOpts.opacity = 0.1
            markerOpts.fillOpacity = 0.1
        }
        return circleMarker(latLng, markerOpts);
    }

    // Text inside marker cirle
    bindMarkerTooltip(marker:L.Marker,startPt:PointType,opts:Partial<L.TooltipOptions>,isSelected:boolean){
        if ( this.isMuted(startPt) ) {
            opts.className += " disabled-point-tooltip"
        }
        (marker as L.Marker).bindTooltip(startPt.name,opts);
    }

    // Text below marker circle
    addInfoMarkerLabel(pt:PointType, ptState: { isViaPt:boolean, isHaPt:boolean, isSelected:boolean},extraMarkerOpts:Partial<L.MarkerOptions>){
        const ptLabelTxt = this.buildLabel( pt, ptState)
        if ( ptLabelTxt ) {
            if ( pt.latitude !== undefined && pt.longitude !== undefined ) {
                let divClasses = ptState.isSelected?'info-div-selected':'';
                if ( this.isMuted(pt) ) {
                    divClasses += " disabled-point-tooltip";
                }
                const infoMarker = L.marker([pt.latitude, pt.longitude], {
                icon: new L.DivIcon({ className: 'info-div-icon',
                    html: `<div class="${divClasses}">${ptLabelTxt}</div>`
                }), ...extraMarkerOpts});
                this.infoMarkers.push( infoMarker)
            }
        }
    }

    setSelectedPoint = ( point : PointType, bEmitChange = true ) => this.setSelectedPointById( point?.id, bEmitChange)

    setSelectedPointById = ( pointId : string, bEmitChange = true ) => {
        const index = this.points.findIndex(sp=>sp.id==pointId)
        if ( index >= 0 ) {
            const point = this.points[index]
            const bIndexChanged = this.selectedIndex != index
            this.selectedIndex = index
            this.addPathSegments()
            if ( bIndexChanged && bEmitChange) {
                this.pointSelected.emit(point)
            }
        } else {
            this.selectedIndex = -1
            this.addPathSegments()
        }
    }

    buildLabel( pt: PointType, context: {isViaPt:boolean, isHaPt: boolean }) {
        var ptLabelTxt : string
        const { isViaPt, isHaPt } = context

        if ( !isViaPt ) {
            if ( isHaPt && pt.pointId ) {
                ptLabelTxt = pt.pointId
            } else {
                ptLabelTxt = pt.exPointId
            }
        }
        return ptLabelTxt
    }

    getSelectedPoint() {
        return this.points[this.selectedIndex]
    }

    initGridGroup() {

        if ( !this.gridEnabled || !this.gridGroup) {
            // assume user deselected grid in Layers control of map
            return
        }

        try{
            this.gridGroup.clearLayers()

            const zoom = this.map.getZoom()

            let cellWidth = 0
            if ( zoom > 19 ) {
                cellWidth = 1
            }
            /* TODO - cellWidths are not correct for 5 and 10 meters below (fix math)
            else if ( zoom > 17) {
              cellWidth = 5
            } else if ( zoom > 15 ) {
              cellWidth = 10
            }*/

            if ( cellWidth > 0 ) {
                const proj = L.CRS.EPSG3857;
                var centerPoint = proj.project(this.centerLatLng);
                var secOfLat = cellWidth / Math.cos(this.centerLatLng.lat * Math.PI/180);
                const leftGridX = -150*cellWidth
                const rightGridX = 150*cellWidth
                const topGridY = 150*cellWidth
                const bottomGridY = -150*cellWidth
                for (var i = leftGridX; i <= rightGridX; i+=cellWidth) {
                    var projectedVerticalStartPt = centerPoint.floor().add([i*secOfLat,topGridY]);
                    var projectedVeritcalEndPt = centerPoint.floor().add([i*secOfLat,bottomGridY]);
                    var verticalStartPt = proj.unproject(projectedVerticalStartPt);
                    var verticalEndPt = proj.unproject(projectedVeritcalEndPt);
                    var projectedHorizontalStartPt = centerPoint.floor().add([rightGridX,i*secOfLat]);
                    var projectedHorizontalEndPt = centerPoint.floor().add([leftGridX,i*secOfLat]);
                    var horizontalStartPt = proj.unproject(projectedHorizontalStartPt);
                    var horizontalEndPt = proj.unproject(projectedHorizontalEndPt);

                    this.gridGroup.addLayer(L.polyline([horizontalStartPt, horizontalEndPt], {color: '#aaa5', weight: 1}));
                    this.gridGroup.addLayer(L.polyline([verticalStartPt, verticalEndPt], {color: '#aaa5', weight: 1}))
                }

                this.gridGroup.bringToBack()
            }
        } catch ( error ) {
            console.error('Failed initializing leaflet grid for scout path')
            throw error
        }
    }
}
